// src/pages/Profile.js
import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const auth = getAuth();
  const storage = getStorage();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [displayName, setDisplayName] = useState("");
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(
    localStorage.getItem("rr_theme") || "classic-purple"
  );
  const [isPremium, setIsPremium] = useState(false);
  const [planType, setPlanType] = useState(null);

  // Themes
  const themeOptions = [
    { id: "classic-purple", label: "Classic Purple", premium: false },

    { id: "rose-blossom", label: "Rose Blossom", premium: true },
    { id: "sunset-ember", label: "Sunset Ember", premium: true },
    { id: "arctic-frost", label: "Arctic Frost", premium: true },
    { id: "forest-emerald", label: "Forest Emerald", premium: true },
    { id: "cyber-neon", label: "Cyber Neon", premium: true },
    { id: "sandstone-gold", label: "Sandstone Gold", premium: true },
    { id: "ocean-tide", label: "Ocean Tide", premium: true },
    { id: "royal-ruby", label: "Royal Ruby", premium: true },
    { id: "lunar-silver", label: "Lunar Silver", premium: true },
    { id: "pastel-sky", label: "Pastel Sky", premium: true },
    { id: "midnight-carbon", label: "Midnight Carbon", premium: true },

    // NEW (12 more) to make 24 total including Classic Purple
    { id: "lavender-mist", label: "Lavender Mist", premium: true },
    { id: "mint-breeze", label: "Mint Breeze", premium: true },
    { id: "peach-glow", label: "Peach Glow", premium: true },
    { id: "sky-ice", label: "Sky Ice", premium: true },
    { id: "deep-indigo", label: "Deep Indigo", premium: true },
    { id: "obsidian-violet", label: "Obsidian Violet", premium: true },
    { id: "carbon-blue", label: "Carbon Blue", premium: true },
    { id: "plum-noir", label: "Plum Noir", premium: true },
    { id: "amber-dusk", label: "Amber Dusk", premium: true },
    { id: "terracotta-rose", label: "Terracotta Rose", premium: true },
    { id: "olive-shadow", label: "Olive Shadow", premium: true },
    { id: "mocha-sunset", label: "Mocha Sunset", premium: true },
  ];

  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const unsub = onSnapshot(userRef, async (snap) => {
      try {
        if (snap.exists()) {
          const data = snap.data();

          setDisplayName(data.displayName || user.email);

          // Backwards compatible: support both avatarUrl and photoURL
          setAvatarUrl(data.avatarUrl || data.photoURL || user.photoURL || "");

          setTheme(data.theme || "classic-purple");
          setIsPremium(!!data.premium);
          setPlanType(data.planType || null);

          document.documentElement.setAttribute(
            "data-theme",
            data.theme || "classic-purple"
          );
        } else {
          // First-time doc: set both avatarUrl and photoURL
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.email,
            avatarUrl: user.photoURL || "",
            photoURL: user.photoURL || "",
            theme: "classic-purple",
            premium: false,
          });
        }
      } catch (err) {
        console.error("Error loading profile snapshot:", err);
        setMessage("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [user]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    setUploading(true);
    setMessage("");

    try {
      // Unique file name to avoid browser caching old avatar
      const uniqueName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `avatars/${user.uid}/${uniqueName}`);

      console.log("Uploading avatar to:", storageRef.fullPath);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      console.log("Avatar uploaded. URL:", url);

      // Update both avatarUrl and photoURL for full compatibility
      await updateDoc(doc(db, "users", user.uid), {
        avatarUrl: url,
        photoURL: url,
      });

      // Immediate UI update (no need to wait for onSnapshot)
      setAvatarUrl(url);
      setMessage("Profile picture updated!");
    } catch (err) {
      console.error("Avatar upload failed:", err);
      setMessage(
        "Upload failed." + (err && err.message ? ` (${err.message})` : "")
      );
    } finally {
      setUploading(false);
    }
  };

  const handleNameUpdate = async () => {
    if (!newName.trim() || !user) return;

    await updateDoc(doc(db, "users", user.uid), { displayName: newName });
    setEditing(false);
    setMessage("Display name updated!");
  };

  const handleThemeChange = async (newTheme) => {
    if (!user) return;

    if (!isPremium && newTheme !== "classic-purple") {
      setMessage("Premium themes require ReflectRise Premium.");
      return;
    }

    setTheme(newTheme);
    localStorage.setItem("rr_theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);

    await updateDoc(doc(db, "users", user.uid), { theme: newTheme });
    setMessage("Theme updated!");
  };

  if (loading) return <div className="card">Loading profile...</div>;
  if (!user) return <div className="card">Please sign in.</div>;

  return (
    <div
      className="card"
      style={{
        maxWidth: "750px",
        margin: "0 auto",
        background: "var(--card)",
        color: "var(--text)",
        padding: "2rem",
        borderRadius: "18px",
        boxShadow: "0 8px 25px rgba(0,0,0,0.45)",
        textAlign: "center",
      }}
    >
      <h2 style={{ color: "var(--primary)", marginBottom: "0.4rem" }}>
        Profile
      </h2>
      <p style={{ opacity: 0.8, marginBottom: "1.6rem" }}>
        Manage your ReflectRise account
      </p>

      {/* Avatar */}
      <img
        src={
          avatarUrl || "https://cdn-icons-png.flaticon.com/512/1077/1077114.png"
        }
        alt="avatar"
        style={{
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          objectFit: "cover",
          marginBottom: "1rem",
          boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
        }}
      />

      <label
        className="btn"
        style={{
          background: "var(--primary)",
          color: "#fff",
          cursor: "pointer",
          padding: "0.6rem 1rem",
          borderRadius: "10px",
          marginBottom: "1.6rem",
          display: "inline-block",
        }}
      >
        {uploading ? "Uploading..." : "Change Avatar"}
        <input
          type="file"
          accept="image/*"
          onChange={handleAvatarUpload}
          style={{ display: "none" }}
        />
      </label>

      {/* Email */}
      <p style={{ marginBottom: "0.6rem" }}>
        <strong>Email:</strong> {user.email}
      </p>

      {/* Display Name */}
      <div style={{ marginBottom: "2rem" }}>
        <strong>Display Name:</strong>

        {!editing ? (
          <>
            <div style={{ marginTop: "0.4rem", marginBottom: "0.6rem" }}>
              {displayName}
            </div>
            <button
              className="btn secondary"
              onClick={() => {
                setEditing(true);
                setNewName(displayName);
              }}
            >
              Edit
            </button>
          </>
        ) : (
          <div
            style={{
              marginTop: "0.6rem",
              display: "flex",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <input
              className="input"
              style={{
                maxWidth: "180px",
                padding: "0.5rem",
              }}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button className="btn" onClick={handleNameUpdate}>
              Save
            </button>
            <button className="btn secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Plan section */}
      <h3 style={{ color: "var(--primary)", marginBottom: "0.8rem" }}>
        Your Plan
      </h3>

      <div
        style={{
          background: "rgba(255,255,255,0.05)",
          padding: "1.2rem",
          borderRadius: "12px",
          marginBottom: "1.6rem",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        {isPremium ? (
          <>
            <p style={{ margin: 0, fontSize: "1.1rem" }}>
              💎 <strong>Premium Member</strong>
            </p>
            <p style={{ opacity: 0.7, marginBottom: "0.8rem" }}>
              Plan: {planType || "Active"}
            </p>

            <button
              className="btn"
              style={{
                background: "var(--primary)",
                padding: "0.7rem 1rem",
                borderRadius: "10px",
                color: "#fff",
              }}
              onClick={() => navigate("/manage-subscription")}
            >
              Manage Subscription
            </button>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: "1.1rem" }}>
              ❌ <strong>Free Plan</strong>
            </p>
            <p style={{ opacity: 0.7, marginBottom: "0.8rem" }}>
              Upgrade to unlock premium themes, analytics and more.
            </p>

            <button
              className="btn"
              style={{
                background: "var(--primary)",
                padding: "0.7rem 1rem",
                borderRadius: "10px",
                color: "#fff",
              }}
              onClick={() => navigate("/premium")}
            >
              Upgrade to Premium
            </button>
          </>
        )}
      </div>

      {/* Themes */}
      <h3 style={{ color: "var(--primary)", marginBottom: "0.8rem" }}>
        Theme
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: "0.6rem",
          marginBottom: "1.4rem",
        }}
      >
        {themeOptions.map((option) => {
          const locked = !isPremium && option.premium;
          const active = theme === option.id;

          return (
            <button
              key={option.id}
              onClick={() => (locked ? null : handleThemeChange(option.id))}
              style={{
                padding: "0.55rem",
                borderRadius: "10px",
                background: active ? "var(--primary)" : "rgba(255,255,255,0.07)",
                border: active
                  ? "1px solid var(--primary-2)"
                  : "1px solid rgba(255,255,255,0.15)",
                opacity: locked ? 0.45 : 1,
                cursor: locked ? "not-allowed" : "pointer",
                color: active ? "#fff" : "var(--text)",
                transition: "0.2s",
              }}
            >
              {option.label}
              {locked && " 🔒"}
            </button>
          );
        })}
      </div>

      {message && (
        <p style={{ marginTop: "1.2rem", color: "var(--primary)" }}>{message}</p>
      )}
    </div>
  );
}
